"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";
import { usePostHog } from "posthog-js/react";
import { useActiveOrg } from "@/features/active-org/_context/ActiveOrgContext";
import { EMPTY_QUERY_SETTLE_MS, PALETTE_EVENTS, loggableQuery } from "../_logic/analytics";

interface PaletteAnalytics {
  /** The palette was opened. `source` uses the store's own vocabulary. */
  trackOpened: (source: "hotkey" | "icon") => void;
  /**
   * A row was chosen. `query` is empty when the user was browsing, not
   * searching, and `rank` is null when the row was not in the ranked list at
   * all (a Recent row can be an event the resting list filters out).
   */
  trackSelected: (params: { itemId: string; group: string; query: string; rank: number | null }) => void;
  /**
   * A query is showing no results. Safe to call on every keystroke: it waits for
   * the query to settle and never reports the same one twice in a session.
   */
  reportEmptyQuery: (query: string) => void;
  /** Cancels a pending empty-query report, e.g. because results arrived. */
  cancelEmptyQuery: () => void;
  /** The user took the zero-result path into the AI chat. */
  trackChatHandoff: (query: string) => void;
}

/**
 * The palette's analytics, as a set of functions rather than a posthog client.
 *
 * Kept as a hook so the org id and the settle timer live in one place, and so
 * the component never sees the analytics client at all. Every call is a no-op
 * outside production, where `usePostHog()` returns undefined.
 *
 * @returns The tracking functions.
 */
export function usePaletteAnalytics(): PaletteAnalytics {
  // Hooks
  const posthog = usePostHog();
  const { activeOrg } = useActiveOrg();

  // The queries already reported this page-load, so backspacing through a word
  // and typing it again does not report it twice. Keyed by org as well as text:
  // the same words in a different org are a different search against a
  // different registry, and every event carries `org_id` precisely so the two
  // can be told apart.
  const reported = useRef<Set<string>>(new Set());
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Derived values
  const orgId = activeOrg?.id ?? null;

  // Handlers
  const cancelEmptyQuery = useCallback(() => {
    if (settleTimer.current) clearTimeout(settleTimer.current);
    settleTimer.current = null;
  }, []);

  const reportEmptyQuery = useCallback(
    (raw: string) => {
      cancelEmptyQuery();
      const query = loggableQuery(raw);
      if (!query) return;
      const seenKey = `${orgId ?? "none"}:${query}`;
      if (reported.current.has(seenKey)) return;

      settleTimer.current = setTimeout(() => {
        reported.current.add(seenKey);
        posthog?.capture(PALETTE_EVENTS.searchEmpty, { query, query_length: query.length, org_id: orgId });
      }, EMPTY_QUERY_SETTLE_MS);
    },
    [cancelEmptyQuery, orgId, posthog]
  );

  // A pending report must not outlive the component. The palette is mounted for
  // the app's lifetime in practice, but a timer that fires into an unmounted
  // tree is a leak whether or not anyone has hit it yet.
  useEffect(() => cancelEmptyQuery, [cancelEmptyQuery]);

  return useMemo(
    () => ({
      trackOpened: (source) => posthog?.capture(PALETTE_EVENTS.opened, { source, org_id: orgId }),
      trackSelected: ({ itemId, group, query, rank }) =>
        posthog?.capture(PALETTE_EVENTS.selected, {
          item_id: itemId,
          group,
          // The query is what makes a selection legible: "someone searched
          // 'redline' and took Contracts" is a working synonym, and an item
          // only ever reached by browsing is one the keywords never serve.
          query: loggableQuery(query) ?? "",
          searched: query.trim().length > 0,
          rank,
          org_id: orgId,
        }),
      reportEmptyQuery,
      cancelEmptyQuery,
      trackChatHandoff: (query) =>
        posthog?.capture(PALETTE_EVENTS.chatHandoff, { query: loggableQuery(query) ?? "", org_id: orgId }),
    }),
    [cancelEmptyQuery, orgId, posthog, reportEmptyQuery]
  );
}
